import { ArrowLeftIcon } from '@phosphor-icons/react'
import { toZonedTime } from 'date-fns-tz'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import { useGetPackageRoomSessionDetails } from '@/api/packages/get-package-room-session-details'
import { PageHeader } from '@/components/layout/page-header'
import { TableGroup } from '@/components/layout/table-group'
import { Icon } from '@/components/ui/icon'
import { Link } from '@/components/ui/link'
import { RowAction } from '@/components/ui/table'
import { Typography } from '@/components/ui/typography'
import { DEFAULT_TZ, EMPTY_VALUE } from '@/constants'
import { popupFactory } from '@/factories'
import { PackageModel } from '@/models'
import { PackageDetailsModal } from '@/pages/packages/package-details-modal'
import { getPackageSessionDetailsColumns } from '@/pages/packages/package-session-details/package-session-details.constants'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { formatDateOnly, formatTimeOnly, getCarrierName } from '@/utils'

import * as S from './package-session-details.styled'

export const PackageSessionDetailsPage = () => {
  const { t } = useTranslation(['packages'])
  const { sessionId } = useParams<{ sessionId: string }>()
  const activeBuilding = useSelector(selectActiveBuilding)
  const timezone = activeBuilding?.timezone ?? DEFAULT_TZ

  const {
    data: sessionData,
    isFetching,
    error,
  } = useGetPackageRoomSessionDetails({
    sessionId: sessionId ? Number(sessionId) : undefined,
  })

  const handleShowInfo = (packageData: PackageModel) => {
    popupFactory.open(PackageDetailsModal, { packageData })
  }

  const columns = useMemo(() => getPackageSessionDetailsColumns({ t, timezone }), [timezone, t])

  const rowActions: RowAction<PackageModel>[] = useMemo(
    () => [
      {
        label: t('packages:sessionDetails.details'),
        onClick: handleShowInfo,
        primary: true,
      },
    ],
    [handleShowInfo, t],
  )

  const { endTime, packages = [] } = sessionData ?? {}

  const formattedDate = endTime
    ? `${formatDateOnly(toZonedTime(endTime, timezone))} ${formatTimeOnly(toZonedTime(endTime, timezone))}`
    : EMPTY_VALUE

  const carrierLabel = packages?.length ? getCarrierName(packages[0].carrier) : EMPTY_VALUE

  return (
    <>
      <PageHeader
        title={t('packages:sessionDetails.title')}
        backButton={
          <Link
            navigateBack
            use="secondary"
            size="small"
            to={{ path: '/dashboard/building-dashboard/packages/log' }}
          >
            <Icon icon={ArrowLeftIcon} />
            {t('packages:sessionDetails.back')}
          </Link>
        }
      />
      <S.InfoCard>
        <S.InfoRow>
          <S.InfoItem>
            <Typography as="span" color="black400" size="text-small">
              {t('packages:sessionDetails.sessionId')}
            </Typography>
            <Typography as="span" color="black600" size="text-small" weight="medium">
              {sessionId}
            </Typography>
          </S.InfoItem>
          <S.InfoItem>
            <Typography as="span" color="black400" size="text-small">
              {t('packages:sessionDetails.date')}
            </Typography>
            <Typography as="span" color="black600" size="text-small" weight="medium">
              {formattedDate}
            </Typography>
          </S.InfoItem>
          <S.InfoItem>
            <Typography as="span" color="black400" size="text-small">
              {t('packages:sessionDetails.carrier')}
            </Typography>
            <Typography as="span" color="black600" size="text-small" weight="medium">
              {carrierLabel}
            </Typography>
          </S.InfoItem>
        </S.InfoRow>
      </S.InfoCard>

      <Typography as="h3" size="text-medium" weight="semibold">
        {t('packages:sessionDetails.packagesHeading')}
      </Typography>
      <TableGroup
        id="package-session-details"
        columns={columns}
        data={packages}
        error={error}
        getRowKey={(packageItem) => packageItem.id}
        loading={isFetching}
        rowActions={rowActions}
      />
    </>
  )
}
